// src/components/portal/EodGlanceWidget.js
// The Overview page's daily glance: what sold on the previous business day, and
// which ingredients took the biggest hit. Deliberately a doorway, not a
// dead end — the whole card navigates through to the full EOD report.
//
// Replaces the unused EodOverviewWidget, which built its date with
// toISOString() and so showed the wrong day either side of midnight UTC.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { eodReportService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import useIsMobile from '../../hooks/useIsMobile';
import { formatQuantity } from '../../utils/units';
import { C } from '../../styles/theme';

const FONT = "'Plus Jakarta Sans', sans-serif";

const TOP_PRODUCTS = 5;
const TOP_INGREDIENTS = 3;

const fmt = (value, currency) => {
    const rounded = Math.round((Number(value) || 0) * 100) / 100;
    return `${currency || ''}${Math.abs(rounded).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
};

// Local time, never toISOString(): a business day is a local calendar date, and
// parsing it at midday sidesteps the DST edge that midnight parsing hits.
const formatBusinessDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(`${dateStr}T12:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const Shimmer = ({ height = 16, width = '100%' }) => (
    <div style={{
        height, width, borderRadius: 6,
        background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
        backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite',
    }} />
);

const Shell = ({ children, onClick, isMobile }) => (
    <div
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
        } : undefined}
        onMouseEnter={onClick ? (e) => { e.currentTarget.style.borderColor = C.greenBorder; } : undefined}
        onMouseLeave={onClick ? (e) => { e.currentTarget.style.borderColor = C.border; } : undefined}
        style={{
            background: C.white, borderRadius: 14, border: `1px solid ${C.border}`,
            padding: isMobile ? 16 : 20, fontFamily: FONT,
            cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s',
        }}
    >
        {children}
    </div>
);

const EodGlanceWidget = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { getCurrency } = useAuth();
    const isMobile = useIsMobile();
    const currency = getCurrency();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await eodReportService.getLastClosedReport();
                if (!cancelled) setReport(data);
            } catch (err) {
                console.error('Failed to load last closed business day:', err);
                if (!cancelled) setReport(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const goToEod = () => navigate('/portal/end-of-day');

    const title = (
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            {t('previous_business_day') || 'Previous Business Day'}
        </div>
    );

    if (loading) {
        return (
            <Shell isMobile={isMobile}>
                {title}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
                    {[1, 2, 3].map(i => <Shimmer key={i} height={28} />)}
                </div>
            </Shell>
        );
    }

    // 204 from the endpoint — the tenant has traded no closed business day yet.
    if (!report) {
        return (
            <Shell isMobile={isMobile}>
                {title}
                <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight}
                        strokeWidth="1.5" style={{ marginBottom: 8 }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 10 }}>
                        {t('eod_no_closed_day') || 'No closed business day yet'}
                    </div>
                    <div
                        onClick={goToEod}
                        style={{
                            fontSize: 12, fontWeight: 600, color: C.green, cursor: 'pointer',
                        }}
                    >
                        {t('go_to_end_of_day') || 'Go to End of Day'} &rarr;
                    </div>
                </div>
            </Shell>
        );
    }

    const products = (report.products || []).slice(0, TOP_PRODUCTS);
    const stockWatch = ((report.ingredients || {}).usage || []).slice(0, TOP_INGREDIENTS);

    return (
        <Shell isMobile={isMobile} onClick={goToEod}>
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 8, marginBottom: 14,
            }}>
                {title}
                <span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>
                    {formatBusinessDate(report.sales_date)}
                </span>
            </div>

            {products.length === 0 ? (
                <div style={{ fontSize: 13, color: C.mutedLight, padding: '12px 0' }}>
                    {t('eod_no_products_sold') || 'Nothing was sold on this business day.'}
                </div>
            ) : (
                products.map((p, i) => (
                    <div key={p.product_code} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 12, padding: '8px 0',
                        borderBottom: i < products.length - 1 ? `1px solid ${C.borderLight}` : 'none',
                    }}>
                        <div style={{
                            fontSize: 13, fontWeight: 600, color: C.text,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{p.product_name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
                                &times;{p.quantity}
                            </span>
                            <span style={{ fontSize: 12, color: C.muted, minWidth: 60, textAlign: 'right' }}>
                                {fmt(p.revenue, currency)}
                            </span>
                        </div>
                    </div>
                ))
            )}

            {/* Stock watch — omitted when the tenant has no inventory feature or
                nothing moved, rather than showing an empty row. */}
            {stockWatch.length > 0 && (
                <div style={{
                    marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`,
                }}>
                    <div style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.8, color: C.muted, marginBottom: 6,
                    }}>{t('stock_watch') || 'Stock Watch'}</div>
                    <div style={{ fontSize: 12, color: C.slate }}>
                        {stockWatch.map(i => `${i.name} ${formatQuantity(i.quantity, i.unit)}`).join(' · ')}
                    </div>
                </div>
            )}
        </Shell>
    );
};

export default EodGlanceWidget;
